import * as React from 'react';

import { Button, Card, cn, LocalizedDate } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { Clock, AlertCircle, XCircle, LucideExternalLink } from 'lucide-react';
import { useLocation } from 'wouter';

import { executionRunCancelAtom } from '../../_graphql/mutationExecutionRunCancel';
import { useTranslations } from '../../i18n/useTranslations';
import { automationsRoutes } from '../../routes';
import { ExecutionRunRecord } from '../../types';

import { ExecutionStatusBadge } from './ExecutionStatusBadge';

type ExecutionRunCardProps = {
  run: ExecutionRunRecord;
  className?: string;
  onCancelled?: () => void;
};

const formatDuration = (durationMs: number | null): string => {
  if (durationMs === null) return '-';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const formatExecutionDuration = (ms: number | null): string => {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

export const ExecutionRunCard: React.FC<ExecutionRunCardProps> = ({
  run,
  className,
  onCancelled,
}) => {
  const t = useTranslations();
  const [, navigate] = useLocation();

  const duration = formatDuration(run.durationMs);
  const [cancelMutation, cancelExecution] = useAtom(executionRunCancelAtom);

  const canCancel = run.status === 'Pending' || run.status === 'Running';

  const handleCancel = async () => {
    if (!canCancel) return;

    const confirmed = window.confirm(t.dialogs.cancelExecution);
    if (!confirmed) return;

    try {
      await cancelExecution({ id: run.id });
      onCancelled?.();
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <Card className={cn('@ExecutionRunCard', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <ExecutionStatusBadge status={run.status} />
            {run.isTest && (
              <span className="rounded bg-neutral/10 px-2 py-0.5 text-xs">
                {t.executions.test}
              </span>
            )}
            <span className="text-xs text-neutral/50">ID: {run.id}</span>
          </div>
          <div className="mb-2 flex items-center gap-4 text-sm text-neutral/60">
            <span className="flex items-center gap-1">
              <Clock className="size-4" />
              {t.executions.triggered} <LocalizedDate date={run.triggeredAt} />
            </span>
            <span>
              {t.executions.duration}: {duration}
            </span>
          </div>
          {(run.nodesExecuted !== null ||
            run.actionsExecuted !== null ||
            run.executionDurationMs !== null) && (
            <div className="mb-2 flex gap-4 text-sm text-neutral/60">
              {run.nodesExecuted !== null && (
                <span>{t.executions.nodes(run.nodesExecuted)}</span>
              )}
              {run.actionsExecuted !== null && (
                <span>{t.executions.actions(run.actionsExecuted)}</span>
              )}
              {run.executionDurationMs !== null && (
                <span>
                  {t.executions.executionTime}:{' '}
                  {formatExecutionDuration(run.executionDurationMs)}
                </span>
              )}
            </div>
          )}
          {run.status === 'Failed' && run.errorMessage && (
            <div className="flex items-start gap-2 rounded bg-failure/10 p-2 text-sm text-failure">
              <AlertCircle className="mt-0.5 size-4 flex-shrink-0" />
              <span className="flex-1">{run.errorMessage}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="small"
            onPress={() =>
              navigate(
                automationsRoutes.executionDetail
                  .replace(':id', run.automationId)
                  .replace(':executionId', run.id),
              )
            }
          >
            <LucideExternalLink className="mr-1 size-4" />
            {t.executions.viewDetails}
          </Button>
          {canCancel && (
            <Button
              variant="ghost"
              size="small"
              onPress={handleCancel}
              isDisabled={cancelMutation.isPending}
              className="text-failure hover:bg-failure/10"
            >
              <XCircle className="mr-1 size-4" />
              {t.executions.cancel}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
